import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { BOOKS, IMAGES } from '../data/studio';
import { Reveal, Eyebrow } from './Reveal';

export default function BooksVault() {
  const [reading, setReading] = useState(null);

  return (
    <section id="books" className="relative py-28 lg:py-40 overflow-hidden" data-testid="books-section">
      <div className="absolute inset-0 opacity-[0.12]">
        <img src={IMAGES.books} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#02040a] via-[#02040a]/70 to-[#02040a]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>Books &amp; Ideas</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-slate-100 max-w-3xl leading-tight">
            The <span className="text-cyan-300 text-glow-cyan">vault</span> of long-form thought
          </h2>
        </Reveal>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8" style={{ perspective: '1400px' }}>
          {BOOKS.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.12}>
              <div className="group flex flex-col items-center" data-testid={`book-card-${b.id}`}>
                <div className="book-cover-3d relative w-52 h-72 rounded-r-lg rounded-l-sm overflow-hidden border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.7)]"
                  style={{ background: `linear-gradient(150deg, #0b0f19 0%, #02040a 70%), linear-gradient(150deg, ${b.accent}22, transparent)` }}
                >
                  <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 30% 20%, ${b.accent}30, transparent 60%)` }} />
                  <div className="absolute left-0 top-0 bottom-0 w-2.5" style={{ background: `linear-gradient(90deg, ${b.accent}55, transparent)` }} />
                  <div className="relative h-full flex flex-col justify-between p-6">
                    <p className="font-mono-precision text-[9px] tracking-[0.3em] text-slate-500">SIDETRACK PRESS</p>
                    <div>
                      <div className="w-8 h-px mb-4" style={{ background: b.accent }} />
                      <h3 className="font-celestial text-lg leading-snug text-slate-100">{b.title}</h3>
                    </div>
                    <p className="font-mono-precision text-[9px] tracking-[0.3em]" style={{ color: b.accent }}>{b.year} · {b.status}</p>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-white/[0.12] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <div className="w-40 h-4 mt-3 rounded-[50%] blur-md bg-black/70 group-hover:bg-black/50 transition-colors duration-500" />
                <p className="text-sm text-slate-400 leading-relaxed text-center mt-6 max-w-xs">{b.blurb}</p>
                <button
                  onClick={() => setReading(b)}
                  className="mt-5 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 rounded-full border border-white/15 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/50 transition-all duration-300"
                  data-testid={`book-read-sample-${b.id}`}
                >
                  Read sample
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {reading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-[#02040a]/85 backdrop-blur-md"
            onClick={() => setReading(null)}
            data-testid="book-reader-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 180, damping: 22 }}
              className="relative max-w-xl w-full rounded-2xl border border-white/10 bg-[#0b0f19] p-10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="book-reader-modal"
            >
              <button onClick={() => setReading(null)} className="absolute top-5 right-5 text-slate-500 hover:text-slate-200" data-testid="book-reader-close-btn" aria-label="Close reader">
                <X size={18} />
              </button>
              <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em]" style={{ color: reading.accent }}>
                Manuscript sample · {reading.year}
              </p>
              <h3 className="font-celestial text-2xl text-slate-100 mt-4">{reading.title}</h3>
              <div className="w-10 h-px my-6" style={{ background: reading.accent }} />
              <p className="font-celestial text-lg leading-loose text-slate-300/90 italic">
                "{reading.excerpt}"
              </p>
              <p className="mt-8 font-mono-precision text-[10px] tracking-[0.25em] text-slate-600">PAGE 01 · SIDETRACK PRESS</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
